import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/services/auth.service';
import { SalesContactService } from 'src/app/core/services/sales-contact.service';
import { UserProfileService } from 'src/app/core/services/user-profile.service';
import {
  IOnboardingOption,
  ISalesContactRequest,
  IUserProfile,
  SALES_CONTACT_ERROR_CODES,
} from 'src/app/core/interfaces';
import { emailValidator } from 'src/app/shared/validators/email.validator';

/**
 * Shared PrimeNG dialog that collects billing snapshot + optional message and
 * submits to `POST /api/sales-contact-requests` (MVP-9). Used by the dashboard
 * test-phase banner and the `/blocked` soft-block page.
 *
 * Usage (in a parent template):
 *   <app-sales-contact-modal
 *     [visible]="showSalesModal"
 *     (closed)="showSalesModal = false"
 *     (submitted)="onSalesRequestSent()"></app-sales-contact-modal>
 *
 * Pre-fill comes from `UserProfileService.getUserProfile()` — the server
 * returns the stored billing fields on the extended `UserProfileResponse`.
 */
@Component({
  standalone: false,
  selector: 'app-sales-contact-modal',
  templateUrl: './sales-contact-modal.component.html',
  styleUrls: ['./sales-contact-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesContactModalComponent implements OnInit {
  /**
   * Local mirror of the parent's `visible` input used as a two-way binding
   * target for the PrimeNG dialog. Same pattern as `FirstTimeSetPasswordComponent`.
   */
  innerVisible = false;

  @Input()
  set visible(v: boolean) {
    const becameVisible = !this.innerVisible && !!v;
    this.innerVisible = !!v;
    // Refresh pre-fill every time the dialog is opened — billing fields may
    // have been updated elsewhere (e.g. a prior submit from another entry
    // point) and the BehaviorSubject in UserProfileService is shared.
    if (becameVisible) {
      this.prefillFromProfile();
    }
  }
  get visible(): boolean {
    return this.innerVisible;
  }

  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<void>();

  form: FormGroup;
  countries: IOnboardingOption[] = [];
  isSubmitting = false;
  serverError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private salesContactService: SalesContactService,
    private userProfileService: UserProfileService,
    private messageService: MessageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(200)]],
      billingDepartment: ['', [Validators.required, Validators.maxLength(200)]],
      billingStreet: ['', [Validators.required, Validators.maxLength(200)]],
      billingZip: ['', [Validators.required, Validators.maxLength(20)]],
      billingCity: ['', [Validators.required, Validators.maxLength(100)]],
      billingCountryId: [null, [Validators.required]],
      invoicingEmail: ['', [Validators.required, emailValidator(), Validators.maxLength(255)]],
      message: ['', [Validators.maxLength(2000)]],
    });
  }

  ngOnInit(): void {
    this.loadCountries();
    this.prefillFromProfile();
  }

  /**
   * @description Dialog-hidden handler: fires after PrimeNG hides the dialog
   * (X button, Escape, or programmatic close after submit). Resets transient
   * UI state so the next open is clean; keeps the form values intact so a
   * retry after a 502 doesn't wipe what the user typed.
   */
  onDismiss(): void {
    this.isSubmitting = false;
    this.serverError = null;
    this.closed.emit();
  }

  /**
   * @description Submits the form to the backend. On 201 shows a success
   * toast, resets the form, closes the dialog, and emits `submitted`. On
   * 502 shows a retry-able error toast and keeps the dialog open. On 400
   * surfaces per-field errors when the backend returned them.
   */
  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isSubmitting) return;

    this.isSubmitting = true;
    this.serverError = null;

    const dto: ISalesContactRequest = {
      fullName: (this.form.value.fullName as string).trim(),
      billingDepartment: (this.form.value.billingDepartment as string).trim(),
      billingStreet: (this.form.value.billingStreet as string).trim(),
      billingZip: (this.form.value.billingZip as string).trim(),
      billingCity: (this.form.value.billingCity as string).trim(),
      billingCountryId: this.form.value.billingCountryId as number,
      invoicingEmail: (this.form.value.invoicingEmail as string).trim(),
      message: this.form.value.message ? (this.form.value.message as string).trim() : undefined,
    };

    this.salesContactService.submit(dto).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'success',
          detail: this.translate.instant('onboarding.salesContact.successToast'),
          life: 3000,
        });
        // Server upserted billing fields — bust the cached profile so the
        // next read pulls the new values.
        this.userProfileService.clearProfile();
        this.form.reset();
        this.innerVisible = false;
        this.submitted.emit();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting = false;
        const code = err?.error?.error as string | undefined;
        if (code === SALES_CONTACT_ERROR_CODES.EMAIL_FAILED) {
          // ACS dispatch failed → row is rolled back server-side. Keep the
          // dialog open; the user clicks submit again once ACS is reachable.
          const emailFailedMsg = this.translate.instant('onboarding.salesContact.emailSendFailed') as string;
          this.serverError = emailFailedMsg;
          this.messageService.add({
            severity: 'error',
            detail: emailFailedMsg,
            life: 4000,
          });
        } else if (code === SALES_CONTACT_ERROR_CODES.VALIDATION_FAILED) {
          const fields = (err?.error?.fields as Record<string, string>) ?? null;
          if (fields) {
            // Mark individual controls as errored so inline messages appear.
            Object.keys(fields).forEach(f => {
              const ctrl = this.form.get(f);
              if (ctrl) {
                ctrl.setErrors({ server: fields[f] });
                ctrl.markAsTouched();
              }
            });
          }
          this.serverError = this.translate.instant('onboarding.salesContact.genericError');
        } else {
          this.serverError = this.translate.instant('onboarding.salesContact.genericError');
        }
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * @description Loads the country list from the backend. Reuses the same
   * lookup endpoint the registration stepper consumes.
   */
  private loadCountries(): void {
    this.authService.getCountries().subscribe({
      next: (countries: IOnboardingOption[]) => {
        this.countries = countries ?? [];
        this.cdr.markForCheck();
      },
      error: () => {
        // Silent fail — leave the dropdown empty; the required validator
        // blocks submit and the generic error toast will surface on retry.
        this.countries = [];
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * @description Pre-fills the form from the cached user profile so the
   * dialog shows stored billing fields. Any missing field stays empty
   * (required validators still gate submit).
   */
  private prefillFromProfile(): void {
    this.userProfileService.getUserProfile().subscribe({
      next: (profile: IUserProfile | null) => {
        if (!profile) return;
        this.form.patchValue({
          fullName: profile.fullName ?? '',
          billingDepartment: profile.billingDepartment ?? '',
          billingStreet: profile.billingStreet ?? '',
          billingZip: profile.billingZip ?? '',
          billingCity: profile.billingCity ?? '',
          billingCountryId: profile.billingCountryId ?? null,
          invoicingEmail: profile.invoicingEmail ?? profile.email ?? '',
        });
        this.cdr.markForCheck();
      },
      error: () => {
        /* No pre-fill on error — user types values manually. */
      },
    });
  }
}
